// g0-blueprint-snapshot-store.ts — V2-1 (spec V2 §4.1 "Nguồn thẩm quyền" nguồn
// 1, I1 E12).
//
// Bản chụp bytes BLUEPRINT mà G0 được request trên đó:
// `<control root>/g0-blueprint-<sha256>.md`, chụp lúc `requestGate("g0")` (init
// và re-request sau khi BLUEPRINT đổi). Control root nằm NGOÀI worktree ⇒
// worker không sửa được; tên file mang hash và mọi lần đọc đều hash lại bytes
// ⇒ file bị sửa tay chỉ thành "không có nguồn" (⇒ `ask`), không bao giờ thành
// thẩm quyền giả. Chụp là best-effort: lỗi chụp KHÔNG chặn init/recheck — hệ
// quả duy nhất là mission đó không auto được (fail-closed về `ask`).
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { sha256HexOfBytes } from "./blueprint-gate-hash.js";
import { writeFileDurable } from "./durable-file-writer.js";
import { loadCommittedBlueprintBytes } from "./mission-blueprint-committed-bytes-check.js";

export function g0BlueprintSnapshotPath(controlRoot: string, sha256: string): string {
  return join(controlRoot, `g0-blueprint-${sha256.toLowerCase()}.md`);
}

/** Ghi snapshot khi bytes đúng hash mong đợi; `false` khi lệch hash hoặc ghi lỗi. */
export async function writeG0BlueprintSnapshot(
  controlRoot: string,
  bytes: Uint8Array,
  expectedSha256: string,
): Promise<boolean> {
  const actual = sha256HexOfBytes(bytes);
  if (actual !== expectedSha256.toLowerCase()) return false;
  try {
    await writeFileDurable(g0BlueprintSnapshotPath(controlRoot, actual), Buffer.from(bytes));
    return true;
  } catch {
    return false;
  }
}

/** Bytes snapshot của `sha256`, CHỈ khi hash lại khớp; vắng/hỏng/lệch ⇒ `undefined`. */
export async function readG0BlueprintSnapshot(
  controlRoot: string,
  sha256: string,
): Promise<Buffer | undefined> {
  try {
    const bytes = await readFile(g0BlueprintSnapshotPath(controlRoot, sha256));
    return sha256HexOfBytes(bytes) === sha256.toLowerCase() ? bytes : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Init: chụp bytes ĐÃ COMMIT ở `base_sha` (đúng bytes preflight đã kiểm hash),
 * không đọc working tree. Mission null-oid không có gì commit ⇒ không chụp.
 */
export async function snapshotG0BlueprintAtInit(input: {
  controlRoot: string;
  sourceRepo: string;
  sourcePath: string;
  baseSha: string;
  expectedSha256: string;
}): Promise<boolean> {
  try {
    const bytes = await loadCommittedBlueprintBytes(
      input.sourceRepo,
      input.sourcePath,
      input.baseSha,
    );
    if (bytes === undefined) return false;
    return await writeG0BlueprintSnapshot(input.controlRoot, bytes, input.expectedSha256);
  } catch {
    return false;
  }
}

/** Re-request G0 (recheck): chụp file trong worktree nếu bytes đúng hash vừa tính. */
export async function snapshotG0BlueprintFromFile(
  controlRoot: string,
  blueprintPath: string,
  expectedSha256: string,
): Promise<boolean> {
  try {
    return await writeG0BlueprintSnapshot(
      controlRoot,
      await readFile(blueprintPath),
      expectedSha256,
    );
  } catch {
    return false;
  }
}
